import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { useElements, useStripe } from '@stripe/react-stripe-js';
import {
  Stripe,
  StripeElements,
  type ConfirmationToken,
  type StripeError,
} from '@stripe/stripe-js';
import {
  type EmbeddedPaymentElementResult,
  type IntentCreationCallbackParams,
} from '@stripe/stripe-react-native';

import { noop, type IconName } from '@sg/garnish';

import { StripeLogger } from '../../utils';
import { getSelectedPaymentMethod } from './helpers';
import { useLabelAndIconByType, usePaymentMethodChangeHandler } from './hooks';

import type { UseStripePaymentProps } from '../../types';

export const useStripePaymentElement = (props: UseStripePaymentProps) => {
  const { paymentMethods, stripeIntentConfigRef } = props;

  // ─── Stripe ────────────────────────────────────────────────────────────────

  const stripe = useStripe();
  const elements = useElements();

  // ─── State ─────────────────────────────────────────────────────────────────

  const isPaymentAlreadySavedRef = useRef(false);
  const selectedPaymentMethodIdRef = useRef('');
  const [isAddingNewPaymentMethod, setIsAddingNewPaymentMethod] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState('');
  const [selectedPaymentIcon, setSelectedPaymentIcon] = useState<IconName>('IconCreditCard'); // prettier-ignore
  const { isApplePay, isGooglePay, isLink, isCashApp } = getSelectedPaymentMethod(selectedPaymentMethodType); // prettier-ignore

  // ─── Payment Method Preview ───────────────────────────────────────────────

  const createConfirmationToken = useCallback(
    async (shouldSubmit = false) => {
      if (!stripe || !elements) {
        throw new Error('Stripe not initialized');
      }

      if (shouldSubmit) {
        const { error: submitError } = await elements.submit();

        if (submitError) {
          StripeLogger.logSubmitError(submitError);
          throw new Error(submitError.message);
        }
      }

      const { error, confirmationToken } = await stripe.createConfirmationToken({
        elements,
      });

      if (error) {
        StripeLogger.logCreatePaymentMethodError(error);
        throw new Error(error.message);
      }

      return confirmationToken;
    },
    [elements, stripe],
  );

  const createPaymentMethodPreview = useCallback(
    async (shouldSubmit = false) => {
      const confirmationToken = await createConfirmationToken(shouldSubmit);
      return confirmationToken.payment_method_preview;
    },
    [createConfirmationToken],
  );

  const matchConfirmationTokenToIntent = useConfirmationTokenMatching(
    stripe,
    elements,
    createConfirmationToken,
  );

  // ─── Handle Payment Change ─────────────────────────────────────────────────

  const convertTypeToLabelAndIcon = useLabelAndIconByType();
  const handlePaymentElementChange = usePaymentMethodChangeHandler({
    isPaymentAlreadySavedRef,
    selectedPaymentMethodIdRef,
    paymentMethods,
    convertTypeToLabelAndIcon,
    setIsAddingNewPaymentMethod,
    setSelectedPaymentMethod,
    setSelectedPaymentMethodType,
    setSelectedPaymentIcon,
    createPaymentMethodPreview,
  });

  useEffect(() => {
    if (!stripe || !elements) return;

    const paymentElement = elements.getElement('payment');

    if (!paymentElement) return;

    paymentElement.on('change', handlePaymentElementChange);

    return () => {
      paymentElement.off('change', handlePaymentElementChange);
    };
  }, [elements, stripe, handlePaymentElementChange]);

  // ─── Confirm Callback ──────────────────────────────────────────────────────

  const confirmStripe = useCallback(async (): Promise<ConfirmResult> => {
    try {
      if (!stripe || !elements) {
        StripeLogger.logStripeNotInitializedError();
        return toFailure('Stripe not initialized');
      }

      const { error: submitError, selectedPaymentMethod } = await elements.submit();

      if (submitError) {
        StripeLogger.logSubmitError(submitError);
        return toFailure(submitError.message);
      }

      const confirmHandler = stripeIntentConfigRef?.current?.confirmHandler;

      if (!confirmHandler) {
        return toFailure('Stripe confirm handler is not available');
      }

      const isPaymentAlreadySaved = isPaymentAlreadySavedRef.current;
      const confirmationToken = isPaymentAlreadySaved ? undefined : await createConfirmationToken();
      const paymentMethodPreview = confirmationToken?.payment_method_preview;

      // Maps the stripe-react-native interface to web.
      return await new Promise<ConfirmResult>((resolve) => {
        const confirmIntent = async (
          params: IntentCreationCallbackParams,
        ): Promise<ConfirmResult> => {
          if (!params.clientSecret) {
            return toFailure(params.error?.message ?? 'No client secret');
          }

          const matchingToken = await matchConfirmationTokenToIntent(
            params.clientSecret,
            confirmationToken,
          );

          const confirmResult = await stripe.confirmPayment({
            clientSecret: params.clientSecret,
            redirect: redirectCondition,
            ...(!matchingToken ? { elements } : {}),
            confirmParams: {
              ...(matchingToken ? { confirmation_token: matchingToken.id } : {}),
              return_url: defaultRedirect,
            },
          });

          if (confirmResult?.error) {
            return toFailure(confirmResult.error.message, confirmResult.error.code);
          }

          if (confirmResult?.paymentIntent?.status === 'requires_action') {
            return { status: 'canceled' };
          }

          return { status: 'completed' };
        };

        const resolveWithError = (error: unknown) => {
          StripeLogger.logGenericError(toError(error));
          resolve(toFailure(error));
        };

        // `confirmHandler` is async, so its rejection has to be routed back to this promise.
        void Promise.resolve(
          confirmHandler(
            {
              id: selectedPaymentMethodIdRef.current,
              paymentMethodType: selectedPaymentMethod, // used for telemetry only.
            } as never,
            checkIfShouldSaveCard(isPaymentAlreadySavedRef, paymentMethodPreview),
            (params) => {
              void confirmIntent(params).then(resolve, resolveWithError);
            },
          ),
        ).catch(resolveWithError);
      });
    } catch (error) {
      StripeLogger.logGenericError(toError(error));
      return toFailure(error);
    }
  }, [
    stripe,
    elements,
    stripeIntentConfigRef,
    createConfirmationToken,
    matchConfirmationTokenToIntent,
  ]);

  return {
    embeddedPaymentElementView: undefined, // component rendering on web
    isUpdatingTotal: false, // atomic operation on web
    isGooglePay,
    isApplePay,
    isLink,
    isCashApp,
    isAddingNewPaymentMethod,
    selectedPaymentMethodType,
    selectedPaymentMethod,
    selectedPaymentImage: undefined, // svg icon on web
    selectedPaymentIcon,
    confirmStripe,
    updateStripe: noop,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * To be removed after payments-svc release to production.
 * The legacy backend requires `setup_future_usage` to be set to `off_session` in the payment intent.
 * Here we set it only when a non wallet/cash app new card is added.
 * After the backend update this is no longer needed and the payments element will handle it.
 */
function checkIfShouldSaveCard(
  isPaymentAlreadySavedRef: RefObject<boolean>,
  paymentMethodPreview?: ConfirmationToken['payment_method_preview'],
) {
  if (!paymentMethodPreview) return false;

  const isPaymentAlreadySaved = isPaymentAlreadySavedRef.current;
  const isPaymentMethodWallet = Boolean(paymentMethodPreview.card?.wallet);
  const isPaymentMethodCard = paymentMethodPreview.type === 'card' && !isPaymentMethodWallet;
  const shouldSaveCard = isPaymentMethodCard && !isPaymentAlreadySaved;

  return shouldSaveCard;
}

/**
 * To be removed after payments-svc release to production.
 * The legacy backend requires `setup_future_usage` to be set to `off_session` in the elements setup.
 * This updates the elements to match whatever is set on the payment intent.
 */
function useConfirmationTokenMatching(
  stripe: Stripe | null,
  elements: StripeElements | null,
  createConfirmationToken: (shouldSubmit: boolean) => Promise<ConfirmationToken>,
) {
  return useCallback(
    async (clientSecret: string, token?: ConfirmationToken) => {
      if (!token) return;

      if (!stripe || !elements) throw new Error('Stripe not initialized');

      const { paymentIntent, error } = await stripe.retrievePaymentIntent(clientSecret);
      if (error) throw toError(error.message);

      const setupFutureUsage = paymentIntent.setup_future_usage;
      if (!setupFutureUsage || setupFutureUsage === token.setup_future_usage) return token;

      try {
        await elements.update({ setupFutureUsage });
        return await createConfirmationToken(true);
      } finally {
        await elements.update({ setupFutureUsage: null });
      }
    },
    [stripe, elements, createConfirmationToken],
  );
}

/**
 * `stripe-react-native` reports failures as an `Error`, but Stripe.js messages are optional
 * and thrown values are unknown, so everything is normalized to an `Error` at this boundary.
 */
function toError(value: unknown) {
  if (value instanceof Error) {
    return value;
  }

  return new Error(typeof value === 'string' && value ? value : GENERIC_ERROR_MESSAGE);
}

function toFailure(error: unknown, code?: StripeError['code']): ConfirmResult {
  return { status: 'failed', error: toError(error), code };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const redirectCondition = 'if_required' as const;
const defaultRedirect = window.location.origin;
const GENERIC_ERROR_MESSAGE = 'Something went wrong confirming the payment';

// ─── Types ───────────────────────────────────────────────────────────────────

type ConfirmResult = EmbeddedPaymentElementResult & { code?: StripeError['code'] };
